import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoginPage from './components/LoginPage';
import InventoryWizard from './components/InventoryWizard';
import InventoryManager from './components/InventoryManager';
import ManualInventoryEntry from './components/ManualInventoryEntry';
import FurnitureTypesManager from './components/FurnitureTypesManager';
import UserManagement from './components/UserManagement';
import InventoryReports from './components/InventoryReports';
import KnowBe4ScoreWidget from './components/KnowBe4ScoreWidget';
import NotificationInbox from './components/NotificationInbox';
import WelcomeLetterWizard from './components/WelcomeLetterWizard';
import BroadcastBanner from './components/BroadcastBanner';
import BroadcastMessenger from './components/BroadcastMessenger';
import LandingPage from './components/LandingPage';
import Gmail from './components/Gmail';
import Dashboard from './components/Dashboard';
import Links from './components/Links';
import GoogleDriveHub from './components/GoogleDriveHub';
import MyHardware from './components/MyHardware';
import ChangePassword from './components/ChangePassword';
import NewHireWalkthrough from './components/NewHireWalkthrough';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Onboarding from './components/Onboarding';
import TimeOffReport from './components/TimeOffReport';
import KnowBe4Report from './components/KnowBe4Report';
import PreOnboarding from './components/PreOnboarding';
import PositionOpening_NEW from './components/PositionOpening_NEW';
import TimeOff from './components/TimeOff';
import StaffDirectory from './components/StaffDirectory';
import OrgChart from './components/OrgChart';
import HumanResources from './components/HumanResources';
import Facilities from './components/Facilities';
import Timesheet634 from './components/Timesheet634';
import Operations from './components/Operations';
import Security from './components/Security';
import { ChatTab } from './components/RightPane';
import ChatTranscripts from './components/ChatTranscripts';
import GetSupportForm from './components/GetSupportForm';
import ProfilePage from './components/ProfilePage';
import StaffModePicker from './components/StaffModePicker';
import RibbonSidebar from './components/RibbonSidebar';
import './App.css';

// Role hierarchy: owner > admin > supervisor > staff. For now the top three tiers
// share admin-level access, so normalize owner/supervisor -> 'admin' for all the
// existing `role === 'admin'` checks, while preserving the true role in `roleLabel`
// for display + future per-role differentiation. Idempotent (safe to re-apply).
const normalizeUser = (u) => {
  if (!u) return u;
  const roleLabel = u.roleLabel || u.role;
  const role = (roleLabel === 'owner' || roleLabel === 'supervisor') ? 'admin' : roleLabel;
  return { ...u, role, roleLabel };
};

// First-login "new hire" welcome walkthrough is not built out yet — keep it OFF so
// users go straight to the home page after login. Flip to true to re-enable it.
const NEW_HIRE_WALKTHROUGH_ENABLED = true;

function MainApp() {
  // Eager initializer: avoid a 1-frame flash to LandingPage when returning from Google OAuth,
  // AND eagerly stash the token to localStorage + axios defaults so the very first Dashboard
  // calendar fetch has auth (the previous version of this only flipped the isAuthenticated
  // flag, so Dashboard rendered and fetched before the useEffect below wrote the token —
  // calendar widget got a 401 until the user navigated away and back).
  // The existing useEffect (below) still does the full handleLogin work: decode JWT, setUser,
  // fetchClientInfo, fetchStaffList.
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('token');
      if (urlToken) {
        localStorage.setItem('token', urlToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${urlToken}`;
        return true;
      }
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      if (token && savedUser && savedUser !== 'undefined') {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        return true;
      }
      return false;
    } catch {
      return false;
    }
  });
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [user, setUser] = useState(null);
  // Role hierarchy: owner > admin > supervisor > staff. For now the three top tiers
  // share one access level, so `user.role` is normalized to 'admin' for owner+supervisor
  // (see normalizeUser); the true role stays in `user.roleLabel` for display.
  // These gates therefore mean "any elevated tier". To later restrict the owner
  // sections to true owners, switch isOwner to `user?.roleLabel === 'owner'`.
  const isOwner = user?.role === 'admin';
  const canHrStaffing = user?.role === 'admin';
  // System-admin tools (Settings / Integrations / Users): Owner + Admin only,
  // NOT Supervisor. Checks the true role (roleLabel), not the normalized one.
  const isSysAdmin = ['owner', 'admin'].includes(user?.roleLabel);
  const [clientInfo, setClientInfo] = useState(null);
  const [currentView, setCurrentView] = useState('dashboard');
  const [currentViewParams, setCurrentViewParams] = useState(null);
  const [pendingCaseId, setPendingCaseId] = useState(null);
  const [chatTrigger, setChatTrigger] = useState(null);
  const [chatPrefill, setChatPrefill] = useState(null);  // message to pre-fill when opening chat (e.g. "discuss this email")
  const [chatEmailAttach, setChatEmailAttach] = useState(null);  // email snapshot to attach to the next chat message
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showStaffPicker, setShowStaffPicker] = useState(false);
  // Navigation chrome style: 'ribbon' (new, default) or 'classic' (legacy). Presentation only.
  // Default is 'ribbon' so the new sidebar shows for everyone without a per-browser toggle.
  const [uiTheme, setUiTheme] = useState(() => localStorage.getItem('uiTheme') || 'ribbon');
  const changeUiTheme = (t) => { setUiTheme(t); try { localStorage.setItem('uiTheme', t); } catch {} };
  // Links master-detail (ribbon mode): sidebar picks a category, content shows its tiles.
  const [linksCategory, setLinksCategory] = useState(null);
  const openLinksCategory = (label) => { setLinksCategory(label); setCurrentView('links'); };
  const [expandedSections, setExpandedSections] = useState({
    collections: false,
    exhibitions: false,
    visitorServices: false,
    facilities: false,
    humanResources: false,
    operations: false,
    accounting: false,
    reports: false,
    administration: false,
    informationTechnology: false,
    development: false,
    communications: false,
    integrations: false,
    myWorkspace: true,
    apps: false
  });
  
  const [currentTime, setCurrentTime] = useState(new Date());
  const [location, setLocation] = useState({ city: 'Loading...', coords: null });
  const [notificationCount, setNotificationCount] = useState(0);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('sidebarCollapsed') === 'true'
  );
  // Toggle body class so .main-content can respond to collapsed sidebar; persist across reloads.
  useEffect(() => {
    document.body.classList.toggle('sidebar-collapsed', sidebarCollapsed);
    localStorage.setItem('sidebarCollapsed', String(sidebarCollapsed));
    return () => document.body.classList.remove('sidebar-collapsed');
  }, [sidebarCollapsed]);
  const [staffList, setStaffList] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            );
            const data = await response.json();
            const city = data.address.city || data.address.town || data.address.village || 'Prescott';
            setLocation({ city, coords: { latitude, longitude } });
          } catch {
            setLocation({ city: 'Prescott, AZ', coords: { latitude, longitude } });
          }
        },
        () => setLocation({ city: 'Prescott, AZ', coords: null })
      );
    }
  }, []);

  useEffect(() => {
    // 1. Check URL for ?token=... — this is how Google OAuth callback hands us the JWT.
    //    Used to live in LoginPage.jsx; moved here 2026-05-29 so it works regardless of
    //    which unauthenticated page renders (e.g. the new LandingPage).
    const urlParams = new URLSearchParams(window.location.search);
    const oauthToken = urlParams.get('token');
    if (oauthToken) {
      handleLogin({ token: oauthToken });
      // Strip the token from the URL so it doesn't linger in the address bar / history.
      const clean = window.location.pathname + (window.location.hash || '');
      window.history.replaceState({}, document.title, clean);
      return;
    }

    // 2. Otherwise restore a previously-saved session from localStorage.
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser && savedUser !== 'undefined') {
      try {
        const userData = normalizeUser(JSON.parse(savedUser));
        setIsAuthenticated(true);
        setUser(userData);
        if (userData.must_change_password) setMustChangePassword(true);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        fetchClientInfo();
        fetchStaffList();
        // Refresh the stored user from the server so the friendly name (display_name) and
        // other profile fields are current even for sessions saved before they existed.
        axios.get(`${process.env.REACT_APP_API_URL}/staff/me`, { headers: { Authorization: `Bearer ${token}` } })
          .then(res => {
            setUser(prev => {
              // res.data (/staff/me) carries the raw DB role — re-normalize so the
              // elevated tiers stay mapped to 'admin' for access checks.
              const merged = normalizeUser({ ...prev, ...res.data });
              localStorage.setItem('user', JSON.stringify(merged));
              return merged;
            });
          })
          .catch(() => {});
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchNotificationCount();
      const interval = setInterval(fetchNotificationCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchChatUnread();
      const interval = setInterval(fetchChatUnread, 15000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const fetchNotificationCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/notifications/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificationCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  };

  // Total unread chat messages — drives the badge on the sidebar "Chat" item so a new
  // message shows up even when you're on another page. Polled, plus refreshed immediately
  // when a conversation is opened (which marks it read server-side — see ChatTab onRead).
  const fetchChatUnread = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/chat/unread`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatUnreadCount(parseInt(response.data.count) || 0);
    } catch (error) {
      console.error('Error fetching chat unread count:', error);
    }
  };

  const fetchStaffList = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/staff`, { headers: { Authorization: `Bearer ${token}` } });
      setStaffList(res.data);
    } catch (e) {}
  };

  const fetchClientInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/client/info`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { clientCode: 'caam' }
      });
      setClientInfo(response.data);
    } catch (error) {
      console.error('Error fetching client info:', error);
      setClientInfo({ nickname: 'CAAM' });
    }
  };

  const handleLogin = (userData) => {
    const token = userData.token;
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    
    const user = normalizeUser(JSON.parse(jsonPayload));

    setIsAuthenticated(true);
    setUser(user);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    fetchClientInfo();
    // Populate staffList so avatars (sidebar + chat) can resolve the user's google_photo_url
    // immediately after a fresh OAuth login. Previously omitted — caused avatars to fall back
    // to initials on first login because staffList stayed empty until next reload.
    fetchStaffList();
    if (userData.user && userData.user.must_change_password) {
      setMustChangePassword(true);
    }
    navigate('/dashboard');
  };

  const handleLogout = () => {
    // If we're previewing a staff member, "Logout" should drop the preview and
    // restore the admin session, not nuke it.
    if (user?.preview) { exitStaffMode(); return; }
    setIsAuthenticated(false);
    setUser(null);
    setClientInfo(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    delete axios.defaults.headers.common['Authorization'];
    setCurrentView('dashboard');
    navigate('/login');
  };

  // Staff Mode — borrow a staff member's identity (read-only). The admin's own
  // token/user is stashed under admin* keys so Exit is instant and lossless,
  // and survives a page reload. Throws on failure so the picker can show the error.
  const enterStaffMode = async (targetStaffId) => {
    const adminToken = localStorage.getItem('token');
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/staff-mode/enter`,
      { targetStaffId },
      { headers: { Authorization: `Bearer ${adminToken}` } }
    );
    const previewUser = normalizeUser(res.data.user);
    const { token } = res.data;
    if (!localStorage.getItem('adminToken')) {
      localStorage.setItem('adminToken', adminToken);
      localStorage.setItem('adminUser', localStorage.getItem('user') || '');
    }
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(previewUser));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(previewUser);
    setShowStaffPicker(false);
    setShowUserMenu(false);
    setCurrentView('dashboard');
    fetchStaffList();
    navigate('/dashboard');
  };

  const exitStaffMode = () => {
    const adminToken = localStorage.getItem('adminToken');
    const adminUser = localStorage.getItem('adminUser');
    if (!adminToken) {
      // Stash somehow lost — fall back to a clean logout rather than get stuck.
      setIsAuthenticated(false);
      setUser(null);
      setClientInfo(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete axios.defaults.headers.common['Authorization'];
      setCurrentView('dashboard');
      navigate('/login');
      return;
    }
    localStorage.setItem('token', adminToken);
    localStorage.setItem('user', adminUser || '');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    axios.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
    try { setUser(normalizeUser(JSON.parse(adminUser))); } catch { setUser(null); }
    setShowUserMenu(false);
    setCurrentView('dashboard');
    fetchStaffList();
    navigate('/dashboard');
  };

  const navigateTo = (view, params = null) => {
    setCurrentView(view);
    setCurrentViewParams(params);
    if (params) setPendingCaseId({ value: params, ts: Date.now() });
    setShowUserMenu(false);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatTime = () => {
    return currentTime.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = () => {
    return currentTime.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    const firstInitial = user.first_name ? user.first_name[0] : '';
    const lastInitial = user.last_name ? user.last_name[0] : '';
    return (firstInitial + lastInitial).toUpperCase() || user.username[0].toUpperCase();
  };

  const getUserDisplayName = () => {
    if (!user) return 'User';
    return user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username;
  };

  const getUserRole = () => {
    if (!user) return 'Staff';
    return user.role === 'admin' ? 'Administrator' : 'Staff';
  };

  const getViewTitle = () => {
    const titles = {
      'dashboard': 'Overview',
      'chat': 'Chat',
      'inventory': 'Office Inventory',
      'add-item': 'Add Inventory Item',
      'facilities': 'Facilities Management',
      'accounting': 'Accounting',
      'rentals': 'Venue Rentals',
      'notifications': 'Notifications',
      'welcome-letter': 'Issue Welcome Letter',
      'equipment-assignment': 'Equipment Assignment',
      'broadcast-messenger': 'Broadcast Messenger',
      'user-management': 'User Management',
      'chat-transcripts': 'Chat Transcripts',
      'get-help': 'Helpdesk',
      'office-management': 'Office Management',
      'google-drive': 'Google Drive',
      'pre-onboarding': 'Openings',
      'onboarding': 'Employee Onboarding',
      'staff-directory': 'Staff Directory',
      'time-off': 'Time Off / Leave',
      'org-chart': 'Org Chart',
      'human-resources': 'Human Resources',
      'facilities': 'Facilities',
      'operations': 'Operations',
      'security': 'Security',
      'asana': 'Asana',
      'links': 'Links',
      'gmail': 'Gmail',
      'reports': 'Reports & Analytics',
      'time-off-report': 'Time Off Report',
      'knowbe4-report': 'KnowBe4 Report',
      'my-hardware': '',
      'timesheet': 'Timesheet'
    };
    return titles[currentView] || 'Overview';
  };

  const renderContent = () => {
    switch (currentView) {
      case 'forms':
        return <div style={{padding:'40px',textAlign:'center'}}><h2>Forms - Coming Soon</h2></div>;
      case 'timesheet':
        return <Timesheet634 currentUser={user} />;
      case 'my-profile':
        return <ProfilePage user={user} onSaved={(u) => {
          const merged = { ...user, ...u };
          setUser(merged);
          localStorage.setItem('user', JSON.stringify(merged));
        }} />;
      case 'account-settings':
        return <div style={{padding:'40px',maxWidth:'720px'}}>
          <h2 style={{marginTop:0,fontSize:'24px',fontWeight:600,color:'#1f2937'}}>Account Settings</h2>
          <div style={{marginTop:'24px',padding:'18px 20px',border:'1px solid #e8ecf2',borderRadius:'12px'}}>
            <div style={{fontWeight:600,fontSize:'14px',color:'#1f2937'}}>Sidebar style</div>
            <p style={{color:'#5F5E5A',fontSize:'12.5px',lineHeight:1.6,margin:'6px 0 12px'}}>Choose how the navigation menu is displayed. This only changes the menu's appearance — every feature stays exactly the same.</p>
            <div style={{display:'inline-flex',gap:'8px'}}>
              {['classic','ribbon'].map(t => (
                <button key={t} onClick={() => changeUiTheme(t)}
                  style={{padding:'8px 16px',borderRadius:'9px',fontSize:'13px',fontWeight:600,cursor:'pointer',
                    border: uiTheme===t ? '1px solid #6E4A16' : '1px solid #e2e8f0',
                    background: uiTheme===t ? '#6E4A16' : '#fff',
                    color: uiTheme===t ? '#fff' : '#33415c'}}>
                  {t==='classic' ? 'Classic' : 'Ribbon (new)'}
                </button>
              ))}
            </div>
          </div>
          <p style={{color:'#888780',fontSize:'12px',marginTop:'24px'}}>Password changes and notification preferences will be available in an upcoming release.</p>
        </div>;
      case 'help-support':
        return <div style={{padding:'40px',maxWidth:'720px'}}><h2 style={{marginTop:0,fontSize:'24px',fontWeight:600,color:'#1f2937'}}>Help &amp; Support</h2><p style={{color:'#5F5E5A',fontSize:'13px',lineHeight:1.7}}>Need help with the CAAM Staff Portal? Reach out to IT at <a href="mailto:it@caamuseum.org" style={{color:'#BA7517'}}>it@caamuseum.org</a> or use the chat panel to message Michael directly.</p></div>;
      case 'gmail':
        return <Gmail onDiscuss={(name, email) => { setChatTrigger(name); setChatPrefill('Can we discuss this email?'); setChatEmailAttach(email); navigateTo('chat'); }} />;
      case 'google-drive':
        return <GoogleDriveHub />;
      case 'dashboard':
        return <Dashboard user={user} navigateTo={navigateTo} />;
      case 'links':
        return <Links category={uiTheme === 'ribbon' ? linksCategory : null} />;
      case 'inventory':
        if (!isSysAdmin) return <div className="coming-soon"><h2>Access Denied</h2><p>This feature is only available to Owners and Admins.</p></div>;
        return <InventoryManager />;
      case 'add-item':
        return <ManualInventoryEntry />;
      case 'user-management':
        if (!isSysAdmin) return <div className="coming-soon"><h2>Access Denied</h2><p>This feature is only available to Owners and Admins.</p></div>;
        return <UserManagement />;
      case 'chat-transcripts':
        if (!isSysAdmin) {
          return <div className="coming-soon"><h2>Access Denied</h2><p>This feature is only available to Michael Emmetti and Jon Lamirault.</p></div>;
        }
        return <ChatTranscripts />;
      case 'get-help':
        // Simple Get Support form (all users) — submits an email request to the MSP intake.
        return <GetSupportForm currentUser={user} />;
      case 'reports':
        if (!isOwner) {
          return <div className="coming-soon">
            <h2>Access Denied</h2>
            <p>This feature is only available to Michael Emmetti and Jon Lamirault.</p>
          </div>;
        }
        return <InventoryReports />;
      case 'time-off-report': {
        if (!isOwner) {
          return <div className="coming-soon">
            <h2>Access Denied</h2>
            <p>This report is only available to Michael Emmetti and Jon Lamirault.</p>
          </div>;
        }
        return <TimeOffReport />;
      }
      case 'knowbe4-report': {
        if (!isOwner) {
          return <div className="coming-soon">
            <h2>Access Denied</h2>
            <p>This report is only available to Michael Emmetti and Jon Lamirault.</p>
          </div>;
        }
        return <KnowBe4Report />;
      }
      case 'chat':
        return <ChatTab currentUser={user} staffList={staffList} initialMention={chatTrigger} initialMessage={chatPrefill} attachEmail={chatEmailAttach} onRead={fetchChatUnread} />;
      case 'notifications':
        return <NotificationInbox navigateTo={navigateTo} onCountChange={fetchNotificationCount} />;
      case 'welcome-letter':
        return <WelcomeLetterWizard />;
      case 'broadcast-messenger':
        if (!isSysAdmin) return <div className="coming-soon"><h2>Access Denied</h2><p>This feature is only available to Owners and Admins.</p></div>;
        return <BroadcastMessenger />;
      case 'my-hardware':
        return <MyHardware isAdmin={user?.role === 'admin'} canSeeAll={['memmetti', 'jlamirault', 'sfoley', 'ptaylor'].includes(user?.username)} />;
      case 'pre-onboarding':
        if (!canHrStaffing) return <div className="coming-soon"><h2>Access Denied</h2><p>Openings is limited to Michael Emmetti, Jon Lamirault, Steven Foley, and Haseeb Saeed.</p></div>;
        return <PositionOpening_NEW isAdmin={user?.role === 'admin'} currentUser={user} />;
      case 'onboarding':
        if (!canHrStaffing) return <div className="coming-soon"><h2>Access Denied</h2><p>Onboarding is limited to Michael Emmetti, Jon Lamirault, Steven Foley, and Haseeb Saeed.</p></div>;
        return <Onboarding isAdmin={user?.role === 'admin'} currentUser={user} openCaseId={pendingCaseId} onCaseOpened={() => setPendingCaseId(null)} onOpenChat={(name) => { setChatTrigger(name); navigateTo('chat'); }} />;
      case 'time-off':
        return <TimeOff currentUser={user} />;
      case 'staff-directory':
        return <StaffDirectory currentUser={user} />;
      case 'org-chart':
        return <OrgChart currentUser={user} />;
      case 'human-resources':
        return <HumanResources
          currentUser={user}
          navigateTo={navigateTo}
          pendingCaseId={pendingCaseId}
          onCaseOpened={() => setPendingCaseId(null)}
          onOpenChat={(name) => { setChatTrigger(name); navigateTo('chat'); }}
        />;
      case 'facilities':
        // Access limited to Michael, Jon, Steven, Haseeb (matches menuModel gate 'facilities').
        return user?.role === 'admin'
          ? <Facilities currentUser={user} />
          : <div className="coming-soon"><h2>Facilities</h2><p>This feature is coming soon</p></div>;
      case 'operations': {
        // Explicit 4-person allowlist (matches menuModel gate 'operations').
        const opsUsers = ['memmetti', 'jlamirault', 'sfoley', 'ptaylor'];
        return opsUsers.includes(user?.username)
          ? <Operations currentUser={user} />
          : <div className="coming-soon"><h2>Access Denied</h2><p>This feature is only available to the Operations team.</p></div>;
      }
      case 'security':
        // Elevated tier only (matches menuModel gate 'security').
        return user?.role === 'admin'
          ? <Security currentUser={user} />
          : <div className="coming-soon"><h2>Security</h2><p>This feature is coming soon</p></div>;
      case 'equipment-assignment':
        return <div style={{padding: '24px'}}>
          <iframe 
            src="/onboarding?clientCode=CAAM" 
            style={{width: '100%', height: 'calc(100vh - 120px)', border: 'none', borderRadius: '12px'}}
            title="Equipment Assignment"
          />
        </div>;
      default:
        return <div className="coming-soon">
          <h2>{getViewTitle()}</h2>
          <p>This feature is coming soon</p>
        </div>;
    }
  };

  const onResetPage = window.location.pathname.includes('reset-password');
  if (isAuthenticated && mustChangePassword && !onResetPage) {
    return <ChangePassword onSuccess={() => setMustChangePassword(false)} />;
  }

  if (NEW_HIRE_WALKTHROUGH_ENABLED && isAuthenticated && !mustChangePassword && user && user.setup_completed === false && !onResetPage) {
    return <NewHireWalkthrough user={user} onDone={() => {
      const updated = { ...user, setup_completed: true };
      setUser(updated);
      localStorage.setItem('user', JSON.stringify(updated));
    }} />;
  }

  if (!isAuthenticated) {
    // Login method is decided at RUNTIME by hostname, so ONE image is correct everywhere.
    // Dev hosts (localhost / LAN / Tailscale / *.local) get the username/password form,
    // because Google OAuth can't complete on dev (Google rejects non-public callback
    // origins — see CLAUDE.md §7.5). Production (intranet.caamuseum.org) and ANY other
    // host fall through to the OAuth-only LandingPage — so prod cannot show the password
    // form regardless of which image is deployed. LoginPage also has its own Google button.
    const DEV_HOSTS = ['localhost', '127.0.0.1', '192.168.101.253', '100.67.2.61', 'dev.caam.local'];
    const host = window.location.hostname;
    if (DEV_HOSTS.includes(host) || host.endsWith('.local')) {
      return <LoginPage onLogin={handleLogin} />;
    }
    return <LandingPage />;
  }

  return (
    <>
      {isAuthenticated && <BroadcastBanner onNavigate={navigateTo} />}
      {showStaffPicker && (
        <StaffModePicker onClose={() => setShowStaffPicker(false)} onEnter={enterStaffMode} />
      )}
      {user?.preview && (
        <div style={{ position:'fixed', bottom:'20px', left:'50%', transform:'translateX(-50%)',
                      zIndex:3500, display:'flex', alignItems:'center', gap:'14px',
                      background:'#4c1d95', color:'#fff', padding:'9px 12px 9px 16px',
                      borderRadius:'999px', boxShadow:'0 8px 28px rgba(76,29,149,0.45)',
                      fontSize:'13px', fontWeight:600, maxWidth:'92vw' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0 }}>
            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/>
          </svg>
          <span style={{ whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
            Previewing as <strong>{getUserDisplayName()}</strong>
            <span style={{ opacity:0.7, fontWeight:500 }}> · read-only</span>
          </span>
          <button
            onClick={exitStaffMode}
            style={{ flexShrink:0, background:'#fff', color:'#4c1d95', border:'none',
                     borderRadius:'999px', padding:'6px 13px', fontSize:'12px', fontWeight:700,
                     cursor:'pointer' }}
          >
            Exit Staff Mode
          </button>
        </div>
      )}
      <div className="app-shell">
        <header className="app-header">
          <div className="app-header-logo">
            <div>California</div>
            <div>African American</div>
            <div>Museum</div>
          </div>
          <div className="app-header-actions">
            <div className="app-header-search">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <span className="app-header-search-placeholder">Search portal, files, people…</span>
              <span className="app-header-search-shortcut">⌘ K</span>
            </div>
            <button
              className="app-header-icon-btn"
              onClick={() => navigateTo('notifications')}
              aria-label="View notifications"
              title="View notifications"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/>
                <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>
              </svg>
              {notificationCount > 0 && <span className="app-header-badge">{notificationCount}</span>}
            </button>
            <div className="app-header-clock">
              <div className="app-header-clock-time">{formatTime()}</div>
              <div className="app-header-clock-date">{formatDate()}</div>
            </div>
            <button
              onClick={() => navigateTo('get-help')}
              title="Get IT Support"
              style={{ display:'inline-flex', alignItems:'center', gap:'7px', padding:'8px 15px', background:'#EA6F2E', color:'#fff', border:'none', borderRadius:'8px', fontSize:'13px', fontWeight:700, cursor:'pointer', whiteSpace:'nowrap', boxShadow:'0 1px 2px rgba(0,0,0,0.15)' }}
              onMouseEnter={e => e.currentTarget.style.background='#D45E1F'}
              onMouseLeave={e => e.currentTarget.style.background='#EA6F2E'}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>
              Get Support
            </button>
          </div>
        </header>
        <div className={`app-container ${uiTheme === 'ribbon' ? 'ribbon-mode' : ''}`}>
          {uiTheme === 'ribbon' ? (
            <RibbonSidebar
              user={user}
              currentView={currentView}
              navigateTo={navigateTo}
              staffList={staffList}
              chatUnreadCount={chatUnreadCount}
              onLogout={handleLogout}
              onEnterStaffMode={() => { setShowUserMenu(false); setShowStaffPicker(true); }}
              onOpenLinksCategory={openLinksCategory}
              linksCategory={linksCategory}
            />
          ) : (
          <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarCollapsed(v => !v)}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={sidebarCollapsed ? 'Expand' : 'Collapse'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              {sidebarCollapsed ? <polyline points="9 18 15 12 9 6"/> : <polyline points="15 18 9 12 15 6"/>}
            </svg>
          </button>
          <nav className="sidebar-menu">
            <button
              className={`menu-item ${currentView === 'dashboard' ? 'active' : ''}`}
              onClick={() => navigateTo('dashboard')}
              data-label="Overview"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
              <span>Overview</span>
            </button>

            {/* My Workspace — the staff hub. Apps (nested), plus Inventory, Forms, Links.
                Visible to everyone; admin-only modules live in their own sections below. */}
            <div className="menu-section">
              <button className="section-header" data-label="My Workspace" onClick={() => setExpandedSections(s => ({...s, myWorkspace: !s.myWorkspace}))}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                </svg>
                <span>My Workspace</span>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`chevron ${expandedSections.myWorkspace ? 'expanded' : ''}`}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              {expandedSections.myWorkspace && (
                <div className="submenu">
                  {/* Apps — nested collapsible group */}
                  <div className="menu-section nested-section">
                    <button className="section-header" data-label="Apps" onClick={() => setExpandedSections(s => ({...s, apps: !s.apps}))}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                        <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                      </svg>
                      <span>Apps</span>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`chevron ${expandedSections.apps ? 'expanded' : ''}`}>
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </button>
                    {expandedSections.apps && (
                      <div className="submenu">
                        <button className={`menu-item submenu-item ${currentView === 'chat' ? 'active' : ''}`} onClick={() => navigateTo('chat')}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                          </svg>
                          <span>Chat</span>
                          {chatUnreadCount > 0 && (
                            <span style={{ marginLeft:'auto', minWidth:'18px', height:'18px', padding:'0 5px', borderRadius:'9px', background:'#EA6F2E', color:'#fff', fontSize:'10px', fontWeight:700, display:'inline-flex', alignItems:'center', justifyContent:'center' }}>
                              {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                            </span>
                          )}
                        </button>
                        <button className={`menu-item submenu-item ${currentView === 'google-drive' ? 'active' : ''}`} onClick={() => navigateTo('google-drive')}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                          </svg>
                          <span>Drive</span>
                        </button>
                        <button className={`menu-item submenu-item ${currentView === 'gmail' ? 'active' : ''}`} onClick={() => navigateTo('gmail')}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                            <polyline points="22,6 12,13 2,6"/>
                          </svg>
                          <span>Gmail</span>
                        </button>
                        <a href="https://apps.docusign.com/" target="_blank" rel="noopener noreferrer" className="menu-item submenu-item" style={{textDecoration:'none', color:'inherit'}}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                          <span>DocuSign</span>
                        </a>
                        <a href="https://account.adobe.com/" target="_blank" rel="noopener noreferrer" className="menu-item submenu-item" style={{textDecoration:'none', color:'inherit'}}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="4 20 12 4 20 20"/><line x1="8" y1="14" x2="16" y2="14"/>
                          </svg>
                          <span>Adobe</span>
                        </a>
                        <a href="https://zoom.us/" target="_blank" rel="noopener noreferrer" className="menu-item submenu-item" style={{textDecoration:'none', color:'inherit'}}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                          </svg>
                          <span>Zoom</span>
                        </a>
                        <button className="menu-item submenu-item" disabled style={{cursor:'not-allowed'}}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <circle cx="12" cy="6" r="3"/><circle cx="6" cy="17" r="3"/><circle cx="18" cy="17" r="3"/>
                          </svg>
                          <span>Asana</span>
                          <span className="badge-future">Soon</span>
                        </button>
                        <button className="menu-item submenu-item" disabled style={{cursor:'not-allowed'}}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                            <path d="M9 12l2 2 4-4"/>
                          </svg>
                          <span>KnowBe4</span>
                          <span className="badge-future">Soon</span>
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Inventory — personal assigned hardware/furniture */}
                  <button className={`menu-item submenu-item ${currentView === 'my-hardware' ? 'active' : ''}`} onClick={() => navigateTo('my-hardware')}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                    <span>Inventory</span>
                  </button>
                  {/* Forms */}
                  <button className={`menu-item submenu-item ${currentView === 'forms' ? 'active' : ''}`} onClick={() => navigateTo('forms')}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
                    </svg>
                    <span>Forms</span>
                  </button>
                  {/* Links */}
                  <button className={`menu-item submenu-item ${currentView === 'links' ? 'active' : ''}`} onClick={() => navigateTo('links')}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                    </svg>
                    <span>Links</span>
                  </button>
                </div>
              )}
            </div>
            {user?.role === 'admin' && [
              { label: 'Exhibitions', icon: <><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M21 15l-5-5L5 21"/><circle cx="8.5" cy="8.5" r="1.5"/></> },
              { label: 'Facilities', icon: <><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/></> },
              { label: 'Visitor Services', icon: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></> },
              { label: 'Operations', icon: <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></> },
              { label: 'Accounting', icon: <><rect x="3" y="5" width="18" height="14" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="15" x2="10" y2="15"/></> },
              { label: 'Development', icon: <><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></> },
              { label: 'Communications', icon: <><path d="M3 11l18-8-8 18-2-8-8-2z"/></> }
            ].map(item => (
              <div key={item.label} className="menu-section future">
                <button className="section-header" data-label={item.label} disabled style={{cursor:'not-allowed'}}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">{item.icon}</svg>
                  <span>{item.label}</span>
                  <span className="badge-future">Soon</span>
                </button>
              </div>
            ))}

            {user && (
              <div className="menu-section">
                <button
                  className={`menu-item ${currentView === 'human-resources' ? 'active' : ''}`}
                  onClick={() => navigateTo('human-resources')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  <span>Human Resources</span>
                </button>
              </div>
            )}

            {user?.role === 'admin' && (
              <div className="menu-section">
                <button
                  className={`menu-item ${currentView === 'security' ? 'active' : ''}`}
                  onClick={() => navigateTo('security')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><circle cx="12" cy="11" r="1"/><path d="M12 12v3"/>
                  </svg>
                  <span>Security</span>
                </button>
              </div>
            )}


            {isSysAdmin && (
              <div className="menu-section">
                <button className="section-header" data-label="Administration" onClick={() => toggleSection('administration')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M12 1v6m0 6v6M5.6 5.6l4.2 4.2m4.2 4.2l4.2 4.2M1 12h6m6 0h6M5.6 18.4l4.2-4.2m4.2-4.2l4.2-4.2"/>
                  </svg>
                  <span>Administration</span>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`chevron ${expandedSections.administration ? 'expanded' : ''}`}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                {expandedSections.administration && (
                  <div className="submenu">
                    <button className={`menu-item submenu-item ${currentView === 'inventory' ? 'active' : ''}`} onClick={() => navigateTo('inventory')}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                        <line x1="9" y1="3" x2="9" y2="21"/>
                      </svg>
                      <span>Inventory Management</span>
                    </button>
                    <button className={`menu-item submenu-item ${currentView === 'user-management' ? 'active' : ''}`} onClick={() => navigateTo('user-management')}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
                      </svg>
                      <span>User Management</span>
                    </button>
                    <button className={`menu-item submenu-item ${currentView === 'chat-transcripts' ? 'active' : ''}`} onClick={() => navigateTo('chat-transcripts')}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      </svg>
                      <span>Chat Transcripts</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {isSysAdmin && (
              <div className="menu-section">
                <button className="section-header" data-label="Information Technology" onClick={() => setExpandedSections(s => ({...s, informationTechnology: !s.informationTechnology}))}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                  </svg>
                  <span>Information Technology</span>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`chevron ${expandedSections.informationTechnology ? 'expanded' : ''}`}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                {expandedSections.informationTechnology && (
                  <div className="submenu">
                    <button className={`menu-item submenu-item ${currentView === 'broadcast-messenger' ? 'active' : ''}`} onClick={() => navigateTo('broadcast-messenger')}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      </svg>
                      <span>Broadcast Messenger</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {isOwner && (
              <div className="menu-section">
                <button className="section-header" data-label="Reports &amp; Analytics" onClick={() => setExpandedSections(s => ({...s, reports: !s.reports}))}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M3 3v18h18M18 17V9M13 17V5M8 17v-3"/>
                  </svg>
                  <span>Reports & Analytics</span>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`chevron ${expandedSections.reports ? 'expanded' : ''}`}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                {expandedSections.reports && (
                  <div className="submenu">
                    {user?.role === 'admin' && (
                      <button className={`menu-item submenu-item ${currentView === 'reports' ? 'active' : ''}`} onClick={() => navigateTo('reports')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                          <line x1="9" y1="3" x2="9" y2="21"/>
                        </svg>
                        <span>Inventory</span>
                      </button>
                    )}
                    <button className={`menu-item submenu-item ${currentView === 'time-off-report' ? 'active' : ''}`} onClick={() => navigateTo('time-off-report')}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                        <path d="M9 16l2 2 4-4"/>
                      </svg>
                      <span>Time Off Report</span>
                    </button>
                    {user?.role === 'admin' && (
                      <button className={`menu-item submenu-item ${currentView === 'knowbe4-report' ? 'active' : ''}`} onClick={() => navigateTo('knowbe4-report')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                          <path d="M9 12l2 2 4-4"/>
                        </svg>
                        <span>KnowBe4 Report</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </nav>

          <div className="sidebar-footer">
            <button 
              className="user-profile-button"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <div className="user-avatar-small">
                {(() => {
                  // Prefer Google Workspace headshot (captured on OAuth login, absolute CDN URL),
                  // fall back to manually-uploaded staff photo, fall back to initials.
                  const me = staffList.find(s => s.id === user?.id);
                  if (me?.google_photo_url) {
                    return <img src={me.google_photo_url} alt={getUserInitials()} />;
                  }
                  if (me?.photo_url) {
                    return <img src={`${process.env.REACT_APP_API_URL}${me.photo_url}`} alt={getUserInitials()} />;
                  }
                  return getUserInitials();
                })()}
              </div>
              <div className="user-info-small">
                <div className="user-name-small">{getUserDisplayName()}</div>
                <div className="user-role-small">{getUserRole()}</div>
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            {showUserMenu && (
              <div className="user-dropdown-menu">
                <button className="dropdown-item" onClick={() => { setShowUserMenu(false); navigateTo('my-profile'); }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  My Profile
                </button>
                <button className="dropdown-item" onClick={() => { setShowUserMenu(false); navigateTo('account-settings'); }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                  </svg>
                  Account Settings
                </button>
                <button className="dropdown-item" onClick={() => { setShowUserMenu(false); navigateTo('help-support'); }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  Help &amp; Support
                </button>
                {user?.role === 'admin' && !user?.preview && (
                  <>
                    <div className="dropdown-divider"></div>
                    <button className="dropdown-item" onClick={() => { setShowUserMenu(false); setShowStaffPicker(true); }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="3"/>
                        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/>
                      </svg>
                      Enter Staff Mode
                    </button>
                  </>
                )}
                <div className="dropdown-divider"></div>
                <button className="dropdown-item logout" onClick={handleLogout}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16 17 21 12 16 7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </aside>
          )}

        <main className="main-content">
          {/* Dashboard has its own "Welcome back" header, so the generic top-bar title would be
              redundant there — hide it on dashboard. But staff still get the top bar (for the
              Get Support button); admins keep the clean dashboard with no top bar. */}
          {currentView !== 'dashboard' && currentView !== 'links' && (
          <header className="top-bar">
            <h1 className="top-bar-title">{getViewTitle()}</h1>
            <div className="top-bar-right">
            </div>
          </header>
          )}
          <div className="content-area">
            {renderContent()}
          </div>
        </main>
        </div>
      </div>
    </>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/onboarding" element={<InventoryWizard />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/login" element={<MainApp />} />
        <Route path="/dashboard" element={<MainApp />} />
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<MainApp />} />
      </Routes>
    </Router>
  );
}

export default App;
